import { use, useMemo } from "react";
import { SHConverter } from "@domain/core/SollHaben";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import type { Category } from "@domain/models";
import { logger } from "@domain/infrastructure/logger";
import { type Model, KontoNumUtils } from "@binale-tech/shared";
import { type BWATableRecord } from "./useBWAColumns";
import { CompanyAccountingYearContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { KontoContext } from "@domain/context/accountingData/KontoEntitiesProvider";
import { BuContext } from "@domain/context/BuContext";
import { RecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { PaymentsContext } from "@domain/context/accountingData/PaymentsProvider";

// keyed by Model.Period: 0 = FirstDayOfYear (Saldenvortrag), 1-12 = months, 13 = LastDayOfYear (Abschluss)
const emptyPeriodValues = new Map(new Array(14).fill(0).map((v, idx) => [idx, 0]));

export const useBWAAccountMap = () => {
    const { companyBuTimeframes } = use(BuContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { allRecords } = use(RecordsContext);
    const { year } = use(YearPeriodContext);
    const { categoryOverrides } = use(KontoContext);

    const yearRecords = useMemo(
        () => (allRecords.list || []).filter((record) => record.date.getFullYear() === year && !record.draft),
        [allRecords, year],
    );
    const payments = use(PaymentsContext);
    const bwaData: Map<string, BWATableRecord> = useMemo(() => {
        if (!yearConfig || yearRecords.length === 0) {
            return new Map();
        }
        if (!companyBuTimeframes) {
            return new Map();
        }
        const { skr, kontoExt } = yearConfig;
        const converter = new SHConverter(yearConfig, companyBuTimeframes, payments);
        const defaultCategories =
            BuTaxesSKR.getBuTimeframeYearPeriod(skr, companyBuTimeframes, year, 1)?.defaultCategories || new Map();

        const bwa = new Map<string, BWATableRecord>();

        // userSavedAccounts - collecting user data to retrieve later names for labels later on
        const userSavedAccounts = new Map<string, Category>();

        [...categoryOverrides].forEach((v: Model.IExtNum) => {
            userSavedAccounts.set(v.getExtNum(kontoExt), v as Category);
        });
        const categories = new Map<string, Category>();

        const bwaAdd = (item: { amount: number; isHaben: boolean }, period: number, accountNum: string) => {
            if (!bwa.has(accountNum)) {
                const record: BWATableRecord = {
                    key: Number(accountNum),
                    konto: categories.get(accountNum),
                    periodAmounts: new Map(emptyPeriodValues),
                };
                bwa.set(accountNum, record);
            }
            const pa = bwa.get(accountNum).periodAmounts;

            if (item.isHaben) {
                pa.set(period, pa.get(period) - item.amount);
            } else {
                pa.set(period, pa.get(period) + item.amount);
            }
        };

        yearRecords.forEach((v) => {
            converter.getAllItems(v).forEach((item) => {
                if (!item.konto) {
                    logger.log("empty item, konto: ", item);
                }
                const key = item.konto.getExtNum(kontoExt);
                if (item.konto.num === "") {
                    logger.log("konto num is empty", item.konto.name, item, v);
                    return;
                }
                if (KontoNumUtils.isCreditor({ extNum: item.konto }, kontoExt)) {
                    return;
                }
                if (KontoNumUtils.isDebitor({ extNum: item.konto }, kontoExt)) {
                    return;
                }
                // if it's one of saved categories, use it then
                if (userSavedAccounts.has(key)) {
                    categories.set(key, userSavedAccounts.get(key));
                } else {
                    // otherwise
                    // if it's a default category - use name from the latest default categories
                    const minLenNum = item.konto.getExtNum(0);
                    const konto = defaultCategories.get(Number(minLenNum));
                    categories.set(key, konto);
                }

                bwaAdd(item, v.period, key);
            });
        });
        return bwa;
    }, [yearConfig, yearRecords, companyBuTimeframes, payments, year, categoryOverrides]);
    return bwaData;
};
